import { useParams } from 'react-router-dom';
import { filesize } from 'filesize';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import { DEPLOYMENT_ACTIVE_STATES } from 'types/deployment';
import { OrganizationParams } from 'types/route_params';
import { OrganizationUsageLimits } from 'types/organization_usage_limits';

import { useDeployments } from 'deployments/hooks/useDeployments';
import { useHasAccess } from 'hooks/useHasAccess';
import { useOrganization } from 'hooks/useOrganization';
import { useUser } from 'hooks/useAuth';
import { isBillingAccount } from 'services/is_billing_account';
import { NUMBER_FORMAT } from 'services/number';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useUpdateUsageLimits } from 'organizations/hooks/useUpdateUsageLimits';
import { useUsageLimits } from 'organizations/hooks/useUsageLimits';
import { useUsageStatistics } from 'organizations/hooks/useUsageStatistics';
import { getEffectiveUtilizationLimit } from 'organizations/utils/utilizationLimit';

import { ExternalLink } from 'components/ExternalLink/ExternalLink';
import { FileSizeInput } from 'components/FileSizeInput/FileSizeInput';
import { FormMessage } from 'components/FormMessage/FormMessage';
import { Heading } from 'components/Heading/Heading';
import { Loader } from 'components/Loader/Loader';
import { NotFound } from 'views/NotFound/NotFound';
import { NumberInputProps } from 'components/NumberInput/NumberInput';
import { Pill } from 'components/Pill/Pill';
import { Text } from 'components/Text/Text';
import * as PageLayout from 'components/PageLayout';
import {
  UtilizationLimitDialog,
  UtilizationLimitDialogProps,
  UtilizationLimitFieldValues,
} from 'organizations/components/UtilizationLimitDialog/UtilizationLimitDialog';
import { WorkspaceLimitsTable } from 'organizations/components/WorkspaceLimitsTable/WorkspaceLimitsTable';

import { OverviewLimitCard } from './OverviewLimitCard';

const RECORDED_ANALYTICS_INCREMENT = 10_000;

type OrganizationLimitKey = keyof OrganizationUsageLimits;

function FileSizeInputWrap({ unit, ...props }: NumberInputProps) {
  return <FileSizeInput {...props} displayUnit="GiB" />;
}

export function OrganizationUtilization() {
  const { organizationID } = useParams<OrganizationParams>();
  const { data: organization } = useOrganization(organizationID);
  const account = useUser();
  const [hasAccess] = useHasAccess();

  const { data: usageStatistics, isLoading } = useUsageStatistics();
  const { data: currentPlan } = useCurrentPlan();
  const {
    cloudStorageBytes,
    fileProcessingMinutes,
    monthlyActiveLiveStreams,
    runningStreams,
    recordingRetentionDays,
    isLoading: isLoadingLimits,
  } = useUsageLimits();
  const { data: activeDeployments } = useDeployments({
    params: {
      limit: 1,
      states: [...DEPLOYMENT_ACTIVE_STATES],
    },
    keepPreviousData: true,
  });

  if (!organization) {
    return <NotFound entity="organization" returnTo={'/organizations'} />;
  }

  // Editing organization-wide limits is a billing action; creating, deleting
  // and distributing limits across workspaces is org management (owner/manager).
  const canEditOrgLimits = isBillingAccount(account, organization.id);
  const canManageWorkspaces = hasAccess('organization_settings');
  const useRunningStreamLimit = Boolean(currentPlan?.use_running_stream_limit);
  const streams = useRunningStreamLimit
    ? runningStreams
    : monthlyActiveLiveStreams;
  const includedStreams = useRunningStreamLimit
    ? currentPlan?.included_running_streams
    : currentPlan?.included_monthly_active_live_streams;
  const includedFileProcessingMinutes =
    currentPlan?.included_file_processing_minutes;

  function validateRecordedAnalyticsLimit(value: number | '' | undefined) {
    if (!Number.isFinite(value)) {
      return true;
    }

    const includedMinutes = Number.isFinite(includedFileProcessingMinutes)
      ? Number(includedFileProcessingMinutes)
      : 0;
    const numericValue = Number(value);

    if (
      numericValue > includedMinutes &&
      (numericValue - includedMinutes) % RECORDED_ANALYTICS_INCREMENT !== 0
    ) {
      return `Limit must be included minutes plus increments of ${NUMBER_FORMAT.format(
        RECORDED_ANALYTICS_INCREMENT
      )} minutes.`;
    }

    return true;
  }

  return (
    <PageLayout.Root size="medium">
      <PageLayout.Container>
        <div className="grid gap-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Heading level="2">Organization utilization</Heading>
              <ExternalLink href="https://docs.lumeo.com/docs/utilization">
                Utilization docs
              </ExternalLink>
            </div>
          </div>

          {currentPlan && (
            <div className="[--text-my:0]">
              <VisuallyHidden>
                <h2>Current plan</h2>
              </VisuallyHidden>
              <FormMessage intent="info" icon="plan">
                <strong>{currentPlan.name}</strong>
                <Text type="paragraph">{currentPlan.description}</Text>
              </FormMessage>
            </div>
          )}

          {/* Custom plan */}
          {currentPlan === null && (
            <div className="[--text-my:0]">
              <VisuallyHidden>
                <h2>Current plan</h2>
              </VisuallyHidden>

              <FormMessage intent="info" icon="plan">
                <Pill intent="info">Custom plan</Pill>
              </FormMessage>
            </div>
          )}

          <section>
            <VisuallyHidden>
              <h2>Utilization</h2>
            </VisuallyHidden>

            {canEditOrgLimits && (
              <Text size="small" className="mb-2 mt-0">
                Click any limit to review or raise its organization-wide cap.
              </Text>
            )}

            {(isLoading || isLoadingLimits) && (
              <Loader size="small" text="Loading utilization data..." />
            )}

            {!isLoading && !usageStatistics && (
              <Text align="center">
                Utilization data currently unavailable.
                <br />
                Please try again later.
              </Text>
            )}

            <ul className="grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 xl:grid-cols-3">
              {Number.isFinite(streams.usage) && currentPlan !== undefined && (
                <li className="min-w-0">
                  <AdjustableLimitCard
                    title={
                      useRunningStreamLimit
                        ? 'Running live streams'
                        : 'Monthly-active live streams'
                    }
                    usage={Number(streams.usage)}
                    planLimit={streams.planLimit}
                    userLimit={streams.userLimit}
                    limitKey={
                      useRunningStreamLimit
                        ? 'running_stream_limit'
                        : 'monthly_active_live_stream_limit'
                    }
                    limitUnit="streams"
                    includedInPlanInfo={
                      Number.isFinite(includedStreams)
                        ? `${NUMBER_FORMAT.format(
                            Number(includedStreams)
                          )} live streams`
                        : 'Unlimited'
                    }
                    canEdit={canEditOrgLimits}
                  />
                </li>
              )}

              {Number.isFinite(fileProcessingMinutes.usage) &&
                currentPlan !== undefined && (
                  <li className="min-w-0">
                    <AdjustableLimitCard
                      title="Recorded analytics"
                      usage={Number(fileProcessingMinutes.usage)}
                      planLimit={fileProcessingMinutes.planLimit}
                      userLimit={fileProcessingMinutes.userLimit}
                      limitKey="file_processing_minutes_limit"
                      unit="minutes"
                      limitUnit="minutes"
                      includedInPlanInfo={
                        Number.isFinite(includedFileProcessingMinutes)
                          ? `${NUMBER_FORMAT.format(
                              Number(includedFileProcessingMinutes)
                            )} minutes`
                          : 'Unlimited'
                      }
                      limitHelpText={`Recorded analytics limits are set as included minutes plus ${NUMBER_FORMAT.format(
                        RECORDED_ANALYTICS_INCREMENT
                      )}-minute blocks.`}
                      validate={validateRecordedAnalyticsLimit}
                      canEdit={canEditOrgLimits}
                    />
                  </li>
                )}

              {currentPlan?.can_retain_recordings &&
                Number.isFinite(recordingRetentionDays.usage) && (
                  <li className="min-w-0">
                    <AdjustableLimitCard
                      title="Retention"
                      usage={Number(recordingRetentionDays.usage)}
                      planLimit={recordingRetentionDays.planLimit}
                      userLimit={recordingRetentionDays.userLimit}
                      limitKey="recording_retention_days"
                      meterUnit="days"
                      limitUnit="days"
                      info="Lumeo-generated recordings and dashboard metadata are automatically deleted after the retention period."
                      includedInPlanInfo={
                        Number.isFinite(currentPlan.recording_retention_days)
                          ? `${NUMBER_FORMAT.format(
                              Number(currentPlan.recording_retention_days)
                            )} days`
                          : 'Unlimited'
                      }
                      canEdit={canEditOrgLimits}
                    />
                  </li>
                )}

              {Number.isFinite(cloudStorageBytes.usage) &&
                currentPlan !== undefined && (
                  <li className="min-w-0">
                    <AdjustableLimitCard
                      title="Cloud storage"
                      usage={Number(cloudStorageBytes.usage)}
                      planLimit={cloudStorageBytes.planLimit}
                      userLimit={cloudStorageBytes.userLimit}
                      limitKey="cloud_storage_bytes_limit"
                      unit="filesize"
                      limitUnit="GiB"
                      info="Lumeo-generated recordings are excluded from Cloud storage limits."
                      includedInPlanInfo={
                        Number.isFinite(
                          currentPlan?.included_cloud_storage_bytes
                        )
                          ? filesize(
                              Number(currentPlan?.included_cloud_storage_bytes),
                              { base: 2 }
                            )
                          : 'Unlimited'
                      }
                      canEdit={canEditOrgLimits}
                    />
                  </li>
                )}

              {currentPlan && (
                <li className="min-w-0">
                  <OverviewLimitCard
                    title="Running workflow deployments"
                    usage={activeDeployments?.total_elements ?? 0}
                    limit={
                      Number.isFinite(currentPlan.running_deployment_limit)
                        ? Number(currentPlan.running_deployment_limit)
                        : null
                    }
                  />
                </li>
              )}
            </ul>
          </section>

          <WorkspaceLimitsTable canEdit={canManageWorkspaces} />
        </div>
      </PageLayout.Container>
    </PageLayout.Root>
  );
}

type AdjustableLimitCardProps = {
  title: string;
  usage: number;
  planLimit?: number | null;
  userLimit?: number | null;
  limitKey: OrganizationLimitKey;
  unit?: 'filesize' | 'minutes';
  /** Unit used on the card's value line when it differs from `unit`. */
  meterUnit?: string;
  limitUnit: 'streams' | 'minutes' | 'days' | 'GiB';
  includedInPlanInfo: string;
  /** Help text shown behind an info icon next to the card title. */
  info?: string;
  limitHelpText?: string;
  validate?: UtilizationLimitDialogProps['validate'];
  canEdit: boolean;
};

/**
 * Organization-wide limit card. When the account can edit limits, the card
 * itself triggers the utilization limit dialog.
 */
function AdjustableLimitCard({
  title,
  usage,
  planLimit,
  userLimit,
  limitKey,
  unit,
  meterUnit,
  limitUnit,
  includedInPlanInfo,
  info,
  limitHelpText,
  validate,
  canEdit,
}: AdjustableLimitCardProps) {
  const {
    isLoading: isSaving,
    isSuccess,
    mutateAsync: updateUsageLimits,
  } = useUpdateUsageLimits();

  const effectiveLimit = getEffectiveUtilizationLimit(planLimit, userLimit);

  async function handleSave({
    shouldLimitUtilization,
    utilizationLimit,
  }: UtilizationLimitFieldValues) {
    if (shouldLimitUtilization && !Number.isFinite(utilizationLimit)) {
      return Promise.reject(new Error('Unable to set utilization limits.'));
    }

    const res = await updateUsageLimits({
      [limitKey]: shouldLimitUtilization ? Number(utilizationLimit) : null,
    } as Record<OrganizationLimitKey, number | null>);
    return res?.[limitKey] ?? null;
  }

  const card = (
    <OverviewLimitCard
      title={title}
      usage={usage}
      limit={effectiveLimit}
      unit={meterUnit ?? unit}
      info={info}
      interactive={canEdit}
    />
  );

  if (!canEdit) {
    return card;
  }

  return (
    <UtilizationLimitDialog
      item={title}
      usage={usage}
      unit={unit}
      effectiveLimit={effectiveLimit}
      planLimit={planLimit}
      userLimit={userLimit}
      limitUnit={limitUnit}
      includedInPlanInfo={includedInPlanInfo}
      limitHelpText={limitHelpText}
      validate={validate}
      onSave={handleSave}
      isLoading={isSaving}
      isSuccess={isSuccess}
      trigger={card}
      InputComponent={unit === 'filesize' ? FileSizeInputWrap : undefined}
    />
  );
}
